import { SHADOWS, GRADIENTS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import { formatDate } from '../constants/auth-methods';
import { RoleDropdown } from './RoleDropdown';
import type { AccountRequest, KeycloakRole } from '../api/keycloak-admin';

interface RequestCardProps {
  request: AccountRequest;
  appRoles: KeycloakRole[];
  selectedRoles: Set<string>;
  onToggleRole: (userId: string, roleName: string) => void;
  isDropdownOpen: boolean;
  onDropdownOpenChange: (open: boolean) => void;
  actionInProgress: boolean;
  onApprove: (userId: string) => void;
  onDeny: (userId: string) => void;
  isDark: boolean;
  colors: ColorTheme;
}

/** Single request card for AccountRequests page */
export function RequestCard({
  request,
  appRoles,
  selectedRoles,
  onToggleRole,
  isDropdownOpen,
  onDropdownOpenChange,
  actionInProgress,
  onApprove,
  onDeny,
  isDark,
  colors,
}: RequestCardProps): React.JSX.Element {
  const { user } = request;

  return (
    <div
      className="vx-card-row"
      style={{
        backgroundColor: colors.card,
        boxShadow: isDark ? SHADOWS.cardDark : SHADOWS.cardLight,
        borderRadius: '12px',
        padding: '20px 24px',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        opacity: actionInProgress ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      {/* User avatar */}
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
          display: 'grid',
          placeItems: 'center',
          color: '#FFFFFF',
          fontFamily: FONTS.heading,
          fontSize: '18px',
          flexShrink: 0,
        }}
      >
        {(user.firstName?.[0] ?? user.username[0]).toUpperCase()}
      </div>

      {/* User info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: FONTS.heading,
              fontSize: '15px',
              color: colors.text,
              letterSpacing: '0.05em',
            }}
          >
            {user.firstName} {user.lastName}
          </span>
          <span
            style={{
              fontFamily: FONTS.body,
              fontSize: '12px',
              color: colors.textSecondary,
            }}
          >
            @{user.username}
          </span>
        </div>
        <div className="vx-event-row" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px' }}>
          <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary }}>
            {user.email}
          </span>
          <span style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary }}>
            Registered {formatDate(user.createdTimestamp)}
          </span>
        </div>
      </div>

      {/* Role selector */}
      <div className="vx-full-field" style={{ minWidth: 0 }}>
        <RoleDropdown
          userId={user.id}
          appRoles={appRoles}
          selectedRoles={selectedRoles}
          onToggle={onToggleRole}
          isOpen={isDropdownOpen}
          onOpenChange={onDropdownOpenChange}
          disabled={actionInProgress}
          isDark={isDark}
          colors={colors}
        />
      </div>

      {/* Action buttons */}
      <div className="vx-card-actions" style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        <button
          onClick={() => onApprove(user.id)}
          disabled={actionInProgress}
          style={{
            height: '34px',
            padding: '0 16px',
            borderRadius: '6px',
            border: 'none',
            background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
            color: '#FFFFFF',
            fontFamily: FONTS.heading,
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            cursor: actionInProgress ? 'not-allowed' : 'pointer',
          }}
        >
          {actionInProgress ? '...' : 'Approve'}
        </button>
        <button
          onClick={() => onDeny(user.id)}
          disabled={actionInProgress}
          style={{
            height: '34px',
            padding: '0 14px',
            borderRadius: '6px',
            border: `1px solid ${colors.border}`,
            backgroundColor: 'transparent',
            color: colors.textSecondary,
            fontFamily: FONTS.heading,
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            cursor: actionInProgress ? 'not-allowed' : 'pointer',
          }}
        >
          Deny
        </button>
      </div>
    </div>
  );
}
